import { FC, useMemo, useState } from 'react';
import { Avatar, Button, Typography } from 'antd';
import { TbHistory, TbLock, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { BaseSkeleton, EmptySection, OverflowFadeScrollArea, SmallTag, SmallTooltip } from '@/shared';
import { ResourceHistoryPolicy, ResourceVersionListItem } from '@/types';
import { dayjs } from '@/utilities';

import {
    filterHistoryVersions,
    getHistoryActorKey,
    getHistoryActorName,
    groupHistoryVersions,
    HISTORY_MODAL_HEADER_HEIGHT,
    HistoryTimeRange,
} from '../../helpers';
import HistoryFilterToolbar, { HistoryFilterOwner } from './components/HistoryFilterToolbar';

interface HistorySiderProps {
    hasMore: boolean;
    items: ResourceVersionListItem[];
    loading: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    onSelect: (version: ResourceVersionListItem) => void;
    policy?: ResourceHistoryPolicy;
    selectedId?: string;
}

const getActorInitials = (version: ResourceVersionListItem): string => {
    return (
        [version.actor?.firstName, version.actor?.lastName]
            .filter(Boolean)
            .map((part) => part?.charAt(0))
            .join('') || '?'
    );
};

const HistorySiderSkeleton: FC = (): JSX.Element => (
    <div aria-label='Loading version history' style={{ padding: '1rem 0.75rem' }}>
        {Array.from({ length: 6 }, (_, index) => (
            <BaseSkeleton
                key={index}
                active
                avatar={{ size: 'small' }}
                title={{ width: index % 2 ? '66%' : '74%' }}
                paragraph={{ rows: 1, width: index % 2 ? '42%' : '50%' }}
                style={{ marginBottom: '1.25rem' }}
            />
        ))}
    </div>
);

const getRetentionLabel = (policy?: ResourceHistoryPolicy): string => {
    if (!policy?.enabled) {
        return 'Version history requires the Plus plan or higher.';
    }

    if (policy.retentionDays === null) {
        return 'Unlimited version history';
    }

    return `${policy.retentionDays} days of version history`;
};

const HistorySider: FC<HistorySiderProps> = ({
    hasMore,
    items,
    loading,
    loadingMore,
    onLoadMore,
    onSelect,
    policy,
    selectedId,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [search, setSearch] = useState('');
    const [ownerId, setOwnerId] = useState('all');
    const [timeRange, setTimeRange] = useState<HistoryTimeRange>('all');
    const filteredItems = useMemo(
        () => filterHistoryVersions(items, { ownerId, query: search, timeRange }),
        [items, ownerId, search, timeRange],
    );
    const groups = groupHistoryVersions(filteredItems);
    const owners = useMemo<HistoryFilterOwner[]>(() => {
        const ownersById = new Map<string, HistoryFilterOwner>();

        items.forEach((version) => {
            const id = getHistoryActorKey(version);
            ownersById.set(id, {
                avatar: version.actor?.avatar,
                id,
                initials: getActorInitials(version),
                label: getHistoryActorName(version),
            });
        });

        return Array.from(ownersById.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [items]);
    const activeFilterCount = Number(ownerId !== 'all') + Number(timeRange !== 'all');
    const filtersActive = Boolean(search.trim()) || activeFilterCount > 0;
    const currentVersionId = items[0]?.id;

    return (
        <aside
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minWidth: 0,
                borderRight: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorBgContainer,
            }}
        >
            <header
                style={{
                    boxSizing: 'border-box',
                    flex: 'none',
                    height: HISTORY_MODAL_HEADER_HEIGHT,
                    padding: '0.875rem 1rem',
                    borderBottom: `1px solid ${token.colorBorder}`,
                }}
            >
                <strong style={{ display: 'block' }}>Version History</strong>
                <small style={{ color: token.colorTextTertiary }}>{getRetentionLabel(policy)}</small>
            </header>

            <HistoryFilterToolbar
                activeFilterCount={activeFilterCount}
                disabled={loading || items.length === 0}
                onOwnerChange={setOwnerId}
                onReset={() => {
                    setOwnerId('all');
                    setSearch('');
                    setTimeRange('all');
                }}
                onSearchChange={setSearch}
                onTimeRangeChange={setTimeRange}
                ownerId={ownerId}
                owners={owners}
                search={search}
                timeRange={timeRange}
            />

            <OverflowFadeScrollArea
                aria-label='Version history entries'
                containerStyle={{ flex: 1, minHeight: 0 }}
                style={{ padding: '0.5rem 0.625rem 1rem' }}
                surface='container'
            >
                {loading ? (
                    <HistorySiderSkeleton />
                ) : filteredItems.length === 0 ? (
                    <EmptySection
                        height='100%'
                        icon={filtersActive ? <TbSearch /> : <TbHistory />}
                        label={filtersActive ? 'No Matching Versions' : 'No Version History'}
                        description={
                            filtersActive
                                ? 'Try a different search or clear the history filters.'
                                : 'Captured changes to this resource will appear here.'
                        }
                    />
                ) : (
                    groups.map((group) => (
                        <section key={group.key} aria-label={group.label}>
                            <Typography.Text
                                type='secondary'
                                style={{
                                    display: 'block',
                                    padding: '1rem 0.625rem 0.5rem',
                                    fontSize: token.fontSizeSM,
                                }}
                            >
                                {group.label}
                            </Typography.Text>
                            {group.items.map((version) => {
                                const selected = version.id === selectedId;
                                const current = version.id === currentVersionId;
                                const createdAt = dayjs(version.createdAt);

                                return (
                                    <button
                                        key={version.id}
                                        type='button'
                                        aria-current={selected ? 'true' : undefined}
                                        onClick={() => onSelect(version)}
                                        style={{
                                            display: 'grid',
                                            gap: '0.5rem',
                                            width: '100%',
                                            marginBottom: '0.25rem',
                                            padding: '0.75rem',
                                            textAlign: 'left',
                                            color: token.colorText,
                                            background: selected ? token.colorBorder : 'transparent',
                                            border: '1px solid transparent',
                                            borderRadius: token.borderRadiusLG,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                <strong>{createdAt.format('MMM D, YYYY')}</strong>{' '}
                                                <span style={{ color: token.colorTextSecondary }}>
                                                    {createdAt.format('h:mm A')}
                                                </span>
                                            </span>
                                            {current ? (
                                                <SmallTag compact message='Current' styles={{ marginLeft: 0 }} />
                                            ) : null}
                                            {version.locked ? (
                                                <SmallTooltip title='This version is outside your plan retention window.'>
                                                    <TbLock style={{ color: token.colorTextTertiary }} />
                                                </SmallTooltip>
                                            ) : null}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Avatar size='small' src={version.actor?.avatar}>
                                                {getActorInitials(version)}
                                            </Avatar>
                                            <span style={{ color: token.colorTextSecondary }}>
                                                {getHistoryActorName(version)}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </section>
                    ))
                )}
                {hasMore && !loading ? (
                    loadingMore ? (
                        <BaseSkeleton title={false} paragraph={{ rows: 1 }} style={{ margin: '0.75rem' }} />
                    ) : (
                        <Button block onClick={onLoadMore} style={{ marginTop: '0.5rem' }} type='dashed'>
                            Load Older Versions
                        </Button>
                    )
                ) : null}
            </OverflowFadeScrollArea>
        </aside>
    );
};

export default HistorySider;
